/**
 * Service for filter caching decoupled from data endpoints.
 *
 * Pattern:
 * 1. POST filters to /api/v1/common/filters/cache/ → get filter_id
 * 2. GET data endpoint with ?filter_id=<uuid>
 * 3. Middleware expands filter_id before view sees it
 *
 * All data views remain GET-only.
 */

import { clientInstance } from '../utils/http-client'
import { baseAPICall } from './baseAPICall'

export interface FilterCacheResponse {
  filter_id: string
}

// Constants
const CACHE_THRESHOLD_BYTES = 512 // 0.5KB
const PAGINATION_PARAMS = ['page', 'limit', 'page_size'] as const

// Cache management
class FilterCacheManager {
  private pendingRequests = new Map<string, Promise<string>>()
  private cachedFilterIds = new Map<string, string>()

  /**
   * Compute hash for filters (currently uses JSON stringification).
   */
  private computeHash(filters: Record<string, any>): string {
    return JSON.stringify(filters)
  }

  /**
   * Get cached filter_id if exists.
   */
  getCached(filters: Record<string, any>): string | undefined {
    const hash = this.computeHash(filters)
    return this.cachedFilterIds.get(hash)
  }

  /**
   * Check if request is already in-flight.
   */
  getPending(filters: Record<string, any>): Promise<string> | undefined {
    const hash = this.computeHash(filters)
    return this.pendingRequests.get(hash)
  }

  /**
   * Cache a new filter_id and track the pending promise.
   */
  async cacheFilters(filters: Record<string, any>): Promise<string> {
    const hash = this.computeHash(filters)

    // Create new cache request
    const cachePromise = (async () => {
      try {
        const response = await baseAPICall<FilterCacheResponse>(() => {
          return clientInstance.post('common/filters/cache/', { filters })
        })

        const filterId = response.filter_id
        // Store in persistent cache
        this.cachedFilterIds.set(hash, filterId)
        return filterId
      } catch (error) {
        console.error('[Filter Cache] Error caching filters')
        throw error
      }
    })()

    // Track this request
    this.pendingRequests.set(hash, cachePromise)

    // Clean up after completion
    cachePromise.finally(() => {
      this.pendingRequests.delete(hash)
    })

    return cachePromise
  }

  /**
   * Get size of filters in bytes.
   */
  getFilterSize(filters: Record<string, any>): number {
    return JSON.stringify(filters).length
  }
}

// Singleton instance
const cacheManager = new FilterCacheManager()

/**
 * Cache filters on backend and get a UUID.
 *
 * @param filters - The filter object to cache
 * @returns Promise containing the filter_id UUID
 *
 * @example
 * const filterId = await cacheFilters({ distributors: ['1', '2', ...] })
 * // Then use: GET /api/v1/nursing_home/?filter_id=filterId
 */
export async function cacheFilters(
  filters: Record<string, any>
): Promise<string> {
  // Check if we already cached these exact filters
  const existingFilterId = cacheManager.getCached(filters)
  if (existingFilterId) {
    return existingFilterId
  }

  // Check if we already have a pending cache request for these exact filters
  const pendingPromise = cacheManager.getPending(filters)
  if (pendingPromise) {
    return pendingPromise
  }

  // Create new cache request
  return cacheManager.cacheFilters(filters)
}

/**
 * Determines if filters should be cached based on their JSON size.
 * Uses 512 bytes (0.5KB) threshold to avoid URL size limits.
 *
 * @param filters - The filter object
 * @returns true if filters should be cached
 */
export function shouldCacheFilters(filters: Record<string, any>): boolean {
  // Skip if filters is empty
  if (!filters || Object.keys(filters).length === 0) {
    return false
  }

  return cacheManager.getFilterSize(filters) > CACHE_THRESHOLD_BYTES
}

/**
 * Separate pagination parameters from filter parameters.
 */
function separatePaginationParams(params: Record<string, any>): {
  filterParams: Record<string, any>
  paginationParams: Record<string, any>
} {
  const paginationParams: Record<string, any> = {}
  const filterParams: Record<string, any> = {}

  for (const [key, value] of Object.entries(params)) {
    if (PAGINATION_PARAMS.includes(key as any)) {
      if (value !== undefined) {
        paginationParams[key] = value
      }
    } else {
      filterParams[key] = value
    }
  }

  return { filterParams, paginationParams }
}

/**
 * Prepare params for API call using 2-call pattern for large filters.
 *
 * For small params (≤512 bytes): Returns params as-is (for GET request)
 * For large params (>512 bytes): Caches filters first, returns { filter_id: uuid }
 *
 * @param params - The query parameters object
 * @returns Promise resolving to params (with filter_id if cached)
 *
 * @example
 * // Small filters
 * const params = await prepareParamsForAPI({ sort: '-name' })
 * // Returns: { sort: '-name' }
 * // Use: GET /api/v1/nursing_home/?sort=-name
 *
 * // Large filters
 * const params = await prepareParamsForAPI({ distributors: ['1', '2', ...500] })
 * // First POSTs to cache, then returns: { filter_id: 'uuid' }
 * // Use: GET /api/v1/nursing_home/?filter_id=uuid
 */
export async function prepareParamsForAPI(
  params: Record<string, any>
): Promise<Record<string, any>> {
  // If we already have a filter_id, just use it
  if (params.filter_id) {
    return params
  }

  // Separate pagination params from filter params
  const { filterParams, paginationParams } = separatePaginationParams(params)

  // Check if FILTER params (without pagination) are large enough to cache
  if (shouldCacheFilters(filterParams)) {
    const filterId = await cacheFilters(filterParams)
    // Return filter_id + pagination params
    return { filter_id: filterId, ...paginationParams }
  }

  // Small filters - no caching needed, return everything
  return params
}
